<template>
    <div class="book-reload">
        <button
            v-tooltip="$t('bookReload.title')"
            class="book-reload__trigger button button--blank"
            type="button"
            @click="openModal"
        >
            <slot>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="26"
                    viewBox="0 0 512 512.001"
                    width="26"
                    @click.prevent
                >
                    <path
                        fill="#fff"
                        fill-opacity="0.65"
                        d="M348.945 221.64v-96.894c0-2.773-1.28-5.336-3.093-7.363L237.219 3.309C235.19 1.176 232.309 0 229.429 0H57.196C25.398 0 0 25.93 0 57.73v325.684c0 31.8 25.398 57.305 57.195 57.305h135.953C218.863 483.402 265.605 512 318.852 512c80.886 0 146.941-65.734 146.941-146.727.11-70.75-50.688-129.867-116.848-143.632zM240.102 37.458l72.882 76.723h-47.273c-14.086 0-25.61-11.63-25.61-25.715zM57.195 419.375c-19.953 0-35.851-16.008-35.851-35.96V57.73c0-20.062 15.898-36.386 35.851-36.386h161.563v67.12c0 25.93 21.023 47.06 46.953 47.06h61.89v83.34c-3.199-.106-5.761-.427-8.535-.427-37.242 0-71.496 14.301-97.32 36.711H86.223c-5.871 0-10.672 4.801-10.672 10.668 0 5.872 4.8 10.672 10.672 10.672h115.675c-7.578 10.672-13.875 21.344-18.78 33.082H86.222c-5.871 0-10.672 4.801-10.672 10.672 0 5.867 4.8 10.672 10.672 10.672h89.957c-2.668 10.672-4.055 22.516-4.055 34.36 0 19.206 3.734 38.203 10.457 54.21H57.195zm261.766 71.39c-69.149 0-125.387-56.238-125.387-125.386 0-69.149 56.13-125.387 125.387-125.387 69.254 0 125.383 56.238 125.383 125.387 0 69.148-56.235 125.387-125.383 125.387zm0 0"
                    />
                    <path
                        fill="#fff"
                        fill-opacity="0.65"
                        d="M86.223 223.027H194.32c5.871 0 10.672-4.804 10.672-10.672 0-5.87-4.8-10.671-10.672-10.671H86.223c-5.871 0-10.672 4.8-10.672 10.671 0 5.868 4.8 10.672 10.672 10.672zm0 0M326.535 286.625c-2.027-2.133-4.8-3.414-7.789-3.414-2.988 0-5.762 1.281-7.789 3.414l-62.535 67.121c-4.055 4.27-3.734 11.098.535 15.047 4.27 4.055 11.203 3.734 15.258-.531l44.18-47.274v116.528c0 5.87 4.8 10.671 10.671 10.671 5.868 0 10.672-4.8 10.672-10.671V320.988l43.856 47.274c2.136 2.238 4.91 3.414 7.793 3.414 2.558 0 5.12-.961 7.254-2.883 4.27-4.055 4.59-10.777.535-15.047zm0 0"
                    />
                </svg>
            </slot>
        </button>
        <portal v-if="showModal" to="modals">
            <Modal
                :title="$t('bookReload.title')"
                class="book-reload__modal"
                name="book-reload"
                @closed="closeModal"
            >
                <template #content="{ close }">
                    <form
                        ref="form"
                        class="editor-form book-reload__form"
                        @submit.prevent="reloadBook"
                    >
                        <Spinner v-if="loading" />
                        <FileInput
                            style="width: 100%;"
                            accept=".docx"
                            :label="$t('upload.fileLabel')"
                            required
                            name="document"
                            @selected="setFile"
                        />
                        <input type="hidden" name="name" :value="book.name" />
                        <div class="editor-form__row">
                            <div class="editor-form__text">
                                {{ $t('bookReload.notice') }}
                            </div>
                        </div>
                        <div class="modal__confirm">
                            <button
                                type="submit"
                                :disabled="!file"
                                :class="{ 'is-disabled': !file }"
                                class="button modal__confirm-button"
                            >
                                {{ $t('bookReload.button') }}
                            </button>
                            <button
                                type="button"
                                class="button button--gray modal__confirm-button"
                                @click.prevent="closeModal"
                            >
                                {{ $t('confirmModal.cancel') }}
                            </button>
                        </div>
                        <ErrorList :error="error" />
                    </form>
                </template>
            </Modal>
        </portal>
    </div>
</template>

<script>
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { FileInput, ErrorList } from '@speechki-frontend/shared/ui/forms';
import { reloadBook } from '../../api/books';

export default {
    name: 'BookReload',
    components: {
        Modal,
        FileInput,
        ErrorList
    },
    inject: ['registerWsHandler', 'removeWsHandler'],
    data() {
        return {
            showModal: false,
            file: undefined,
            loading: false,
            error: null,
            timeout: null
        };
    },
    computed: {
        book() {
            return this.$store.state.book.book;
        }
    },
    watch: {
        showModal(value) {
            if (value) {
                this.registerWsHandler('create_book', this.onCreateBookMessage);
            } else {
                this.removeWsHandler('create_book', this.onCreateBookMessage);
            }
        }
    },
    beforeUnmount() {
        clearTimeout(this.timeout);
    },
    methods: {
        openModal() {
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
        },
        setFile(file) {
            this.file = file;
        },
        async reloadBook() {
            try {
                this.error = null;
                this.loading = true;

                const data = new FormData(this.$refs.form);

                await reloadBook(data);

                // wait for ws message or timeout
                if (!this.$socket || this.$socket.readyState !== 1) {
                    this.timeout = setTimeout(() => {
                        this.finish();
                    }, 15000);
                }
            } catch (e) {
                this.loading = false;
                this.error = e;
            }
        },
        onCreateBookMessage({ code, params = {} }) {
            if (code === 200) {
                if (params.id && params.id === parseInt(this.$route.params.id)) {
                    this.finish();
                }
            }
        },
        finish() {
            clearTimeout(this.timeout);
            this.loading = false;
            this.$emit('reloaded');
            this.closeModal();
        }
    }
};
</script>

<style lang="scss">
.book-reload {
    margin-bottom: -6px;
}
</style>
